import {
  getDoc,
  doc,
  getDocs,
  getCountFromServer,
  query,
  where,
  collection,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache for total counts to avoid hitting quotas
const countCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Fallback estimates (only used if getCountFromServer fails)
const getEstimatedCount = (collectionName, hasFilters = false) => {
  const estimates = {
    users: hasFilters ? 500 : 5000,
  };
  return estimates[collectionName] || 500;
};

// Get cached count or estimate
const getCachedOrEstimatedCount = async (collectionName, countQuery, hasFilters = false) => {
  const cacheKey = `${collectionName}_${hasFilters ? 'filtered' : 'total'}`;
  const cached = countCache.get(cacheKey);

  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.count;
  }

  // Always try to get real count first, with error handling
  try {
    const snapshotCount = await getCountFromServer(countQuery);
    const count = snapshotCount.data().count;
    countCache.set(cacheKey, { count, timestamp: Date.now() });
    console.log(`✅ Real count retrieved for ${collectionName}: ${count}`);
    return count;
  } catch (error) {
    console.warn(`❌ Count query failed for ${collectionName}, using estimate:`, error.message);
    const estimated = getEstimatedCount(collectionName, hasFilters);
    countCache.set(cacheKey, { count: estimated, timestamp: Date.now() });
    return estimated;
  }
};

export const createListQuery = async (collectionName, {
  pageSize = 100,
  pageIndex = 0,
  filters = [],
  sortBy = [],
  lastVisible = null
} = {}) => {
  let queryConstraints = [];

  // Apply filters
  filters.forEach(filter => {
    const fieldPath = filter._field.segments[0];
    const fieldValue = filter._value;
    let filterFormatted = filter;

    // For text fields like email and name, use contains-like search
    if (fieldPath === 'email' || fieldPath === 'name' || fieldPath === 'id') {
      // For exact matches, use the original filter
      // For partial matches, we'll need to handle this differently
      filterFormatted = filter;
    }
    console.log('filter', filter);
    queryConstraints.push(filterFormatted);
  });

  // Apply sorting
  if (sortBy.length > 0) {
    sortBy.forEach(sort => {
      queryConstraints.push(orderBy(sort.field, sort.direction));
    });
  } else if (filters.length > 0) {
    // If there's a filter, add an orderBy for the filtered field
    filters.forEach(filter => {
      const fieldPath = filter._field.segments.join('.');
      queryConstraints.push(orderBy(fieldPath));
    });
  } else {
    // Default sorting
    queryConstraints.push(orderBy('createdAt', 'desc'));
  }

  console.log('queryConstraints', queryConstraints);
  // Apply pagination
  queryConstraints.push(limit(pageSize));

  if (pageIndex > 0 && lastVisible) {
    queryConstraints.push(startAfter(lastVisible));
  }

  const collectionRef = collection(db, collectionName);
  const q = query(collectionRef, ...queryConstraints);

  // For count query, exclude pagination constraints by rebuilding the query
  let countConstraints = [];
  
  // Add filters
  filters.forEach(filter => {
    const fieldPath = filter._field.segments[0];
    const fieldValue = filter._value;
    let filterFormatted = filter;

    // For text fields like email and name, use exact match
    if (fieldPath === 'email' || fieldPath === 'name' || fieldPath === 'id') {
      filterFormatted = filter;
    }
    countConstraints.push(filterFormatted);
  });

  // Add sorting (needed for some filtered queries)
  if (sortBy.length > 0) {
    sortBy.forEach(sort => {
      countConstraints.push(orderBy(sort.field, sort.direction));
    });
  } else if (filters.length > 0) {
    filters.forEach(filter => {
      const fieldPath = filter._field.segments.join('.');
      countConstraints.push(orderBy(fieldPath));
    });
  }
  
  const countQuery = countConstraints.length > 0 
    ? query(collectionRef, ...countConstraints) 
    : collectionRef;

  const hasFilters = filters.length > 0;
  const totalItems = await getCachedOrEstimatedCount(collectionName, countQuery, hasFilters);

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    console.log('No matching documents.');
    return {
      data: [],
      lastVisible: null,
      pageSize,
      pageIndex,
      sortBy,
      filters,
      totalPages: 0,
      totalItems: 0,
    };
  }

  const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  if (sortBy.length === 0) {
    data.sort((a, b) => b.createdAt - a.createdAt);
  }
  lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

  return {
    data,
    lastVisible,
    pageSize,
    pageIndex,
    sortBy,
    filters,
    totalPages: Math.ceil(totalItems / pageSize),
    totalItems,
  };
};

// Helper function to manually refresh count cache when needed
export const refreshCountCache = async (collectionName) => {
  const cacheKey = `${collectionName}_total`;
  try {
    const collectionRef = collection(db, collectionName);
    const snapshotCount = await getCountFromServer(collectionRef);
    const count = snapshotCount.data().count;
    countCache.set(cacheKey, { count, timestamp: Date.now() });
    console.log(`✅ Count cache refreshed for ${collectionName}: ${count}`);
    return count;
  } catch (error) {
    console.warn(`❌ Failed to refresh count cache for ${collectionName}:`, error.message);
    return null;
  }
};

// Clear cache function
export const clearCountCache = () => {
  countCache.clear();
  console.log('🗑️ Count cache cleared');
};

// Direct function to get real user count (bypassing estimates)
export const getRealUserCount = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const snapshot = await getCountFromServer(usersCollection);
    const count = snapshot.data().count;
    console.log(`✅ Real user count: ${count}`);
    return count;
  } catch (error) {
    console.error('❌ Failed to get real user count:', error);
    throw error;
  }
};